import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Tabs,
  Tab,
  IconButton,
  InputAdornment,
  Chip,
  Divider,
  Switch,
  FormControlLabel,
  CircularProgress,
  Collapse,
  Card,
  CardContent
} from '@mui/material';
import {
  Bot,
  Key,
  Eye,
  EyeOff,
  Save,
  RefreshCw,
  Zap,
  Check,
  X,
  Settings2,
  Code2,
  ChevronDown,
  ChevronUp,
  Plus,
  Info,
  AlertTriangle
} from 'lucide-react';

// Liste des providers disponibles (fallback si l'API ne répond pas)
const DEFAULT_PROVIDERS = [
  { id: 'openai', name: 'OpenAI' },
  { id: 'deepseek', name: 'DeepSeek' },
  { id: 'openrouter', name: 'OpenRouter' },
  { id: 'local', name: 'Local (OpenAI-compatible)' }
];

// Onglet Configuration LLM
function LlmConfigTab() {
  const [config, setConfig] = useState({
    provider: 'openai',
    apiKeySet: false,
    apiKeyMasked: null,
    model: null,
    baseUrl: '',
    availableProviders: DEFAULT_PROVIDERS
  });
  const [newApiKey, setNewApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingModels, setLoadingModels] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchConfig = useCallback(async () => {
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch('/api/settings/llm', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Garder les providers par défaut si l'API n'en renvoie pas
        setConfig({
          ...data,
          availableProviders: data.availableProviders?.length > 0 
            ? data.availableProviders 
            : DEFAULT_PROVIDERS
        });
      } else {
        const err = await response.json().catch(() => ({}));
        setError(err.error || 'Erreur de chargement de la configuration');
      }
    } catch (err) {
      setError('Erreur de chargement de la configuration');
    }
    setLoading(false);
  }, []);

  const fetchModels = useCallback(async () => {
    setLoadingModels(true);
    setError('');
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch('/api/settings/llm/models', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setModels(data.models || []);
      } else {
        const err = await response.json();
        setError(err.error || 'Erreur lors de la récupération des modèles');
      }
    } catch (err) {
      setError('Erreur réseau');
    }
    setLoadingModels(false);
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    if (config.apiKeySet || config.provider === 'local') {
      fetchModels();
    }
  }, [config.apiKeySet, config.provider, fetchModels]);

  const handleProviderChange = async (e) => {
    const provider = e.target.value;
    setConfig(prev => ({ ...prev, provider, model: null }));
    setModels([]);

    // Save provider change
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch('/api/settings/llm', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ provider })
      });
      if (!response.ok && response.status !== 401) {
        const err = await response.json().catch(() => ({}));
        setError(err.error || 'Erreur lors du changement de fournisseur');
      }
    } catch (err) {
      setError('Erreur réseau');
    }
  };

  const handleSaveBaseUrl = async () => {
    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch('/api/settings/llm', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ baseUrl: config.baseUrl })
      });

      if (response.ok) {
        setSuccess('URL enregistrée');
        setTimeout(() => setSuccess(''), 3000);
        fetchModels();
      } else {
        const err = await response.json().catch(() => ({}));
        setError(err.error || 'Erreur lors de l\'enregistrement');
      }
    } catch (err) {
      setError('Erreur réseau');
    }
    setSaving(false);
  };

  const handleSaveApiKey = async () => {
    if (!newApiKey.trim()) return;

    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch('/api/settings/llm', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ apiKey: newApiKey })
      });

      if (response.ok) {
        const data = await response.json();
        setConfig(prev => ({ ...prev, apiKeySet: true, apiKeyMasked: data.apiKeyMasked }));
        setNewApiKey('');
        setSuccess('Clé API enregistrée');
        setTimeout(() => setSuccess(''), 3000);
        fetchModels();
      } else {
        const err = await response.json();
        setError(err.error || 'Erreur lors de l\'enregistrement');
      }
    } catch (err) {
      setError('Erreur réseau');
    }
    setSaving(false);
  };

  const handleModelChange = async (e) => {
    const model = e.target.value;
    setConfig({ ...config, model });

    const token = localStorage.getItem('portalToken');
    await fetch('/api/settings/llm', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ model })
    });

    setSuccess('Modèle sélectionné');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch('/api/settings/llm/test', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setTestResult(data);
    } catch (err) {
      setTestResult({ success: false, error: 'Erreur réseau' });
    }
    setTesting(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" icon={<Check size={20} />}>{success}</Alert>}

      {/* Fournisseur */}
      <Paper sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Box sx={{ p: 1, bgcolor: 'rgba(99, 102, 241, 0.1)', borderRadius: 1.5, color: '#6366f1' }}>
            <Bot size={20} />
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Fournisseur LLM</Typography>
        </Stack>

        <FormControl fullWidth size="small">
          <InputLabel>Fournisseur</InputLabel>
          <Select
            value={config.provider}
            onChange={handleProviderChange}
            label="Fournisseur"
          >
            {config.availableProviders.map(p => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* URL du serveur local */}
      {config.provider === 'local' && (
        <Paper sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Box sx={{ p: 1, bgcolor: 'rgba(124, 58, 237, 0.1)', borderRadius: 1.5, color: '#7c3aed' }}>
              <Code2 size={20} />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>URL du serveur</Typography>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            URL de base de votre serveur local compatible OpenAI (ex: http://localhost:11434, http://192.168.1.100:8080)
          </Typography>

          <Stack direction="row" spacing={2}>
            <TextField
              fullWidth
              size="small"
              placeholder="http://localhost:11434"
              value={config.baseUrl}
              onChange={(e) => setConfig(prev => ({ ...prev, baseUrl: e.target.value }))}
            />
            <Button
              variant="contained"
              onClick={handleSaveBaseUrl}
              disabled={!config.baseUrl.trim() || saving}
              startIcon={saving ? <CircularProgress size={16} /> : <Save size={16} />}
            >
              {saving ? '...' : 'Enregistrer'}
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Clé API */}
      <Paper sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Box sx={{ p: 1, bgcolor: 'rgba(0, 212, 170, 0.1)', borderRadius: 1.5, color: '#00d4aa' }}>
            <Key size={20} />
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Clé API {config.provider === 'local' && <Typography component="span" variant="body2" color="text.secondary">(optionnel)</Typography>}
          </Typography>
          {config.apiKeySet && (
            <Chip label="Configurée" size="small" color="success" sx={{ height: 22 }} />
          )}
        </Stack>

        {config.apiKeySet && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Clé actuelle : {config.apiKeyMasked}
          </Alert>
        )}

        <Stack direction="row" spacing={2}>
          <TextField
            fullWidth
            size="small"
            type={showApiKey ? 'text' : 'password'}
            placeholder={config.apiKeySet ? 'Entrez une nouvelle clé pour remplacer' : 'Entrez votre clé API'}
            value={newApiKey}
            onChange={(e) => setNewApiKey(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowApiKey(!showApiKey)}>
                    {showApiKey ? <EyeOff size={18} /> : <Eye size={18} />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <Button
            variant="contained"
            onClick={handleSaveApiKey}
            disabled={!newApiKey.trim() || saving}
            startIcon={saving ? <CircularProgress size={16} /> : <Save size={16} />}
          >
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </Stack>
      </Paper>

      {/* Sélection du modèle */}
      <Paper sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ p: 1, bgcolor: 'rgba(236, 72, 153, 0.1)', borderRadius: 1.5, color: '#ec4899' }}>
              <Settings2 size={20} />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Modèle</Typography>
          </Stack>
          <IconButton size="small" onClick={fetchModels} disabled={(!config.apiKeySet && config.provider !== 'local') || loadingModels}>
            <RefreshCw size={16} className={loadingModels ? 'animate-spin' : ''} />
          </IconButton>
        </Stack>

        {!config.apiKeySet && config.provider !== 'local' ? (
          <Alert severity="warning">Configurez d'abord la clé API pour voir les modèles disponibles</Alert>
        ) : config.provider === 'local' && !config.baseUrl ? (
          <Alert severity="warning">Configurez d'abord l'URL du serveur local</Alert>
        ) : loadingModels ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">Chargement des modèles...</Typography>
          </Box>
        ) : (
          <FormControl fullWidth size="small">
            <InputLabel>Modèle</InputLabel>
            <Select
              value={config.model || ''}
              onChange={handleModelChange}
              label="Modèle"
            >
              {models.map(m => (
                <MenuItem key={m.id} value={m.id}>
                  {m.name}
                  {m.contextLength && (
                    <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                      ({Math.round(m.contextLength / 1000)}K ctx)
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Paper>

      {/* Test de connexion */}
      <Paper sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ p: 1, bgcolor: 'rgba(245, 158, 11, 0.1)', borderRadius: 1.5, color: '#f59e0b' }}>
              <Zap size={20} />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Test de connexion</Typography>
          </Stack>
          <Button
            variant="outlined"
            onClick={handleTestConnection}
            disabled={(!config.apiKeySet && config.provider !== 'local') || !config.model || testing}
            startIcon={testing ? <CircularProgress size={16} /> : <Zap size={16} />}
          >
            {testing ? 'Test en cours...' : 'Tester'}
          </Button>
        </Stack>

        {testResult && (
          <Alert
            severity={testResult.success ? 'success' : 'error'}
            sx={{ mt: 2 }}
            icon={testResult.success ? <Check size={20} /> : <X size={20} />}
          >
            {testResult.success ? (
              <>Connexion réussie en {testResult.responseTime}ms</>
            ) : (
              testResult.error
            )}
          </Alert>
        )}
      </Paper>
    </Stack>
  );
}

// Onglet Gestion des Agents
function AgentsTab() {
  const [agents, setAgents] = useState([]);
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedAgent, setExpandedAgent] = useState(null);
  const [editingPrompt, setEditingPrompt] = useState({});
  const [saving, setSaving] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchAgents = useCallback(async () => {
    try {
      const token = localStorage.getItem('portalToken');
      const [agentsRes, varsRes] = await Promise.all([
        fetch('/api/settings/agents', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/settings/agents/variables', { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (agentsRes.ok) {
        setAgents(await agentsRes.json());
      }
      if (varsRes.ok) {
        setVariables(await varsRes.json());
      }
    } catch (err) {
      setError('Erreur de chargement');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const handleToggleAgent = async (slug) => {
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/settings/agents/${slug}/toggle`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const updated = await response.json();
        setAgents(prev => prev.map(a => a.slug === slug ? updated : a));
      }
    } catch (err) {
      setError('Erreur lors du toggle');
    }
  };

  const handleSavePrompt = async (slug) => {
    setSaving(prev => ({ ...prev, [slug]: true }));
    setError('');

    try {
      const token = localStorage.getItem('portalToken');
      const promptData = editingPrompt[slug];
      
      const response = await fetch(`/api/settings/agents/${slug}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: promptData.name,
          description: promptData.description,
          systemPrompt: promptData.systemPrompt,
          modelOverride: promptData.modelOverride || null
        })
      });

      if (response.ok) {
        const updated = await response.json();
        setAgents(prev => prev.map(a => a.slug === slug ? updated : a));
        setSuccess('Agent mis à jour');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const err = await response.json();
        setError(err.error || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      setError('Erreur réseau');
    }

    setSaving(prev => ({ ...prev, [slug]: false }));
  };

  const startEditing = (agent) => {
    setEditingPrompt(prev => ({
      ...prev,
      [agent.slug]: {
        name: agent.name,
        description: agent.description || '',
        systemPrompt: agent.systemPrompt,
        modelOverride: agent.modelOverride || ''
      }
    }));
    setExpandedAgent(agent.slug);
  };

  const handlePromptChange = (slug, field, value) => {
    setEditingPrompt(prev => ({
      ...prev,
      [slug]: {
        ...prev[slug],
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" icon={<Check size={20} />}>{success}</Alert>}

      {/* Variables disponibles */}
      <Paper sx={{ p: 3, bgcolor: 'rgba(99, 102, 241, 0.03)', border: '1px solid rgba(99, 102, 241, 0.15)', borderRadius: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Info size={18} color="#6366f1" />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#6366f1' }}>
            Variables disponibles pour les prompts
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {variables.map(v => (
            <Chip
              key={v.name}
              label={v.name}
              size="small"
              variant="outlined"
              sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
              title={v.description}
            />
          ))}
        </Stack>
      </Paper>

      {/* Liste des agents */}
      {agents.map(agent => {
        const isTemplateBuilder = agent.slug === 'template-builder';
        
        return (
        <Card
          key={agent.slug}
          sx={{
            bgcolor: 'rgba(255,255,255,0.02)',
            border: agent.isActive ? '1px solid rgba(0, 212, 170, 0.2)' : '1px solid rgba(255,255,255,0.06)',
            borderRadius: 2
          }}
        >
          <CardContent sx={{ p: 3 }}>
            {/* Header */}
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ 
                  p: 1.5, 
                  bgcolor: agent.isActive ? 'rgba(0, 212, 170, 0.1)' : 'rgba(255,255,255,0.05)', 
                  borderRadius: 2,
                  color: agent.isActive ? '#00d4aa' : 'text.secondary'
                }}>
                  <Bot size={24} />
                </Box>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{agent.name}</Typography>
                    <Chip
                      label={agent.slug}
                      size="small"
                      sx={{ fontFamily: 'monospace', fontSize: '0.65rem', height: 20 }}
                    />
                    {isTemplateBuilder && (
                      <Chip
                        label="Config. par type"
                        size="small"
                        color="warning"
                        sx={{ fontSize: '0.65rem', height: 20 }}
                      />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 500 }}>
                    {agent.description}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={2} alignItems="center">
                <FormControlLabel
                  control={
                    <Switch
                      checked={agent.isActive}
                      onChange={() => handleToggleAgent(agent.slug)}
                      color="primary"
                    />
                  }
                  label={agent.isActive ? 'Actif' : 'Inactif'}
                />
                <IconButton
                  onClick={() => expandedAgent === agent.slug ? setExpandedAgent(null) : startEditing(agent)}
                  sx={{ color: expandedAgent === agent.slug ? 'primary.main' : 'text.secondary' }}
                >
                  {expandedAgent === agent.slug ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </IconButton>
              </Stack>
            </Stack>
            
            {/* Avertissement template-builder */}
            {isTemplateBuilder && expandedAgent !== agent.slug && (
              <Alert 
                severity="warning" 
                icon={<AlertTriangle size={18} />}
                sx={{ mt: 2 }}
              >
                Le prompt de cet agent est maintenant configuré individuellement pour chaque type de projet.
                Accédez à <strong>Paramètres → Types de projets → [Type] → Prompt AI</strong> pour personnaliser.
              </Alert>
            )}

            {/* Expanded content */}
            <Collapse in={expandedAgent === agent.slug}>
              <Divider sx={{ my: 3 }} />
              
              <Stack spacing={3}>
                {/* Nom et Description */}
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Nom"
                    size="small"
                    value={editingPrompt[agent.slug]?.name || ''}
                    onChange={(e) => handlePromptChange(agent.slug, 'name', e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Modèle (override)"
                    size="small"
                    placeholder="Laisser vide pour utiliser le modèle global"
                    value={editingPrompt[agent.slug]?.modelOverride || ''}
                    onChange={(e) => handlePromptChange(agent.slug, 'modelOverride', e.target.value)}
                    sx={{ flex: 1 }}
                  />
                </Stack>

                <TextField
                  label="Description"
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  value={editingPrompt[agent.slug]?.description || ''}
                  onChange={(e) => handlePromptChange(agent.slug, 'description', e.target.value)}
                />

                {/* Prompt système */}
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Code2 size={16} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Prompt Système</Typography>
                  </Stack>
                  <TextField
                    fullWidth
                    multiline
                    rows={12}
                    value={editingPrompt[agent.slug]?.systemPrompt || ''}
                    onChange={(e) => handlePromptChange(agent.slug, 'systemPrompt', e.target.value)}
                    placeholder="Entrez le prompt système de l'agent..."
                    sx={{
                      '& .MuiInputBase-input': {
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        lineHeight: 1.6
                      }
                    }}
                  />
                </Box>

                {/* Actions */}
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={() => setExpandedAgent(null)}
                  >
                    Annuler
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => handleSavePrompt(agent.slug)}
                    disabled={saving[agent.slug]}
                    startIcon={saving[agent.slug] ? <CircularProgress size={16} /> : <Save size={16} />}
                  >
                    {saving[agent.slug] ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                </Stack>
              </Stack>
            </Collapse>
          </CardContent>
        </Card>
        );
      })}

      {/* Info */}
      <Paper sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>À propos des agents AI</Typography>
        <Typography variant="body2" color="text.secondary">
          Les agents AI sont des assistants spécialisés qui utilisent le LLM configuré pour effectuer des tâches spécifiques.
          Chaque agent possède son propre prompt système qui définit son comportement et ses capacités.
          Les variables entre doubles accolades (ex: {"{{SECTION_TYPES}}"}) sont remplacées dynamiquement par les données du système.
        </Typography>
      </Paper>
    </Stack>
  );
}

// Composant principal
function LlmSettings() {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          Configuration AI
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configurez le fournisseur LLM et gérez les agents AI
        </Typography>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3, bgcolor: 'transparent', backgroundImage: 'none' }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minHeight: 48
            }
          }}
        >
          <Tab icon={<Settings2 size={18} />} iconPosition="start" label="Configuration LLM" />
          <Tab icon={<Bot size={18} />} iconPosition="start" label="Agents AI" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && <LlmConfigTab />}
      {activeTab === 1 && <AgentsTab />}
    </Box>
  );
}

export default LlmSettings;
